import {
  getCurrentPractitioner,
  getReportCriticality,
  getReportReviewed,
  markReportReviewed,
} from '@/features/report/services/reportClient'
import type { OrderReportResponse } from '@/types/OrderReportTypes'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'

type Params = {
  orderId: string | undefined
  reports: OrderReportResponse[]
  reportsLoading: boolean
  reportsError: boolean
  reportCaller: string
}

/**
 * Detects whether the latest report for an order is critical and not yet
 * reviewed, and exposes an acknowledge action. Versioned amendments inherit the
 * pipeline's criticality flag per row, so we gate the banner on the single most
 * recent report (the latest published version supersedes earlier ones).
 */
export function useCriticalResultAcknowledgement({
  orderId,
  reports,
  reportsLoading,
  reportsError,
  reportCaller,
}: Params) {
  const [criticalReportId, setCriticalReportId] = useState<string | null>(null)
  const [criticalReportVersions, setCriticalReportVersions] = useState<OrderReportResponse[]>([])
  const [isCriticalAndUnreviewed, setIsCriticalAndUnreviewed] = useState(false)
  const [isConfirmingReview, setIsConfirmingReview] = useState(false)
  const [criticalAcknowledged, setCriticalAcknowledged] = useState(false)
  const [currentPractitionerId, setCurrentPractitionerId] = useState<string | null>(null)

  // Resolve the current user's linked practitioner_id for the reviewed audit record.
  useEffect(() => {
    let cancelled = false
    getCurrentPractitioner()
      .then((result) => {
        if (!cancelled && result.practitionerId) setCurrentPractitionerId(result.practitionerId)
      })
      .catch((err) => {
        Sentry.captureException(err, { tags: { operation: 'order.current-practitioner' } })
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!orderId || criticalAcknowledged) return
    if (reportsLoading || reportsError) return
    if (!reports?.length) return

    let cancelled = false
    const run = async () => {
      try {
        const latest = [...reports].sort((a, b) => {
          if (a.version != null && b.version != null) return b.version - a.version
          return (b.created_at ?? '').localeCompare(a.created_at ?? '')
        })[0]
        if (!latest) return

        let isCritical = false
        let isReviewed = false
        try {
          const [crit, rev] = await Promise.all([
            getReportCriticality(orderId, latest.id, reportCaller),
            getReportReviewed(orderId, latest.id, reportCaller),
          ])
          isCritical = crit.criticality?.toLowerCase() === 'critical'
          isReviewed = rev.reviewed
        } catch {
          return
        }
        if (cancelled) return

        if (isCritical && !isReviewed) {
          setCriticalReportId(latest.id)
          setCriticalReportVersions([latest])
          setIsCriticalAndUnreviewed(true)
        }
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err, { tags: { operation: 'order.report-criticality' } })
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [orderId, criticalAcknowledged, reportCaller, reports, reportsLoading, reportsError])

  const confirmCriticalReview = async () => {
    if (!orderId || !criticalReportId) return
    setIsConfirmingReview(true)
    try {
      await markReportReviewed(orderId, criticalReportId, reportCaller, currentPractitionerId)
      setCriticalAcknowledged(true)
      setIsCriticalAndUnreviewed(false)
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: 'order.report.mark-reviewed' } })
    } finally {
      setIsConfirmingReview(false)
    }
  }

  return {
    isCriticalAndUnreviewed,
    criticalReportVersions,
    criticalAcknowledged,
    isConfirmingReview,
    confirmCriticalReview,
  }
}
